import { queryApp } from '@wix/ambassador-devcenter-v1-app/http';
import { listDraftAppsComponents } from '@wix/ambassador-devcenter-v1-component/http';
import type { Component } from '@wix/ambassador-devcenter-v1-component/types';
import _ from 'lodash';
import * as util from '#packages/util';
import { HttpClient } from '@wix/http-client';
import {
  type GetDeveloperAppsResponse,
  type GetAppSnapshotResponse,
  type GetAppInfoResponse,
  ComponentType,
  type Component as ComponentOld,
} from '@wix/ambassador-app-service-webapp/types';
import { getAppByVersion as getAppByVersionRequest } from '@wix/ambassador-devcenter-apps-v1-app-old/http';
import { queryMyApps } from '@wix/ambassador-devcenter-myapps-v1-my-app/http';
import { queryMarketApp } from '@wix/ambassador-devcenter-market-apps-v1-market-app/http';
import {
  queryAppVersion,
  listLatestProductionVersions,
  listNotes,
} from '@wix/ambassador-devcenter-apps-v1-app-version/http';
import type { AppVersion } from '@wix/ambassador-devcenter-apps-v1-app-version/types';
import { SortOrder } from '@wix/ambassador-devcenter-apps-v1-app-version/types';
import type { VersionsResponse } from '@wix/ambassador-site-apps-server/types';
import experiment from 'experiment';
import type { Account } from '@wix/ambassador-identity-account-v2-account/types';
import { accountServerFacade } from './accountServerFacade';

const APPS_SERVICES_BASE_URL = 'https://dev.wix.com/_api/app-service/v1/';
const appsServiceBaseUrl = `${util.serviceTopology.editorRootUrl}_api/app-service/v1`;
const httpClient = new HttpClient();

interface DeveloperAppData {
  appDefinitionId: string;
  appDefinitionName: string;
  latestVersion: string;
  dateCreated?: string;
  accountId?: string;
}

const fetchLatestVersionMap = async (appIds: string[]) => {
  let appVersionList: AppVersion[] = [];
  const chunkIds = _.chunk(appIds, 50);

  for (const chunk of chunkIds) {
    const appVersions = await fetchListLatestProductionVersions(chunk);

    appVersionList = appVersionList.concat(appVersions);
  }

  return appVersionList;
};

const getAccountOnlyApps = async (
  userId: string,
  accountId: string,
): Promise<DeveloperAppData[]> => {
  const response = await httpClient.request(
    queryApp({
      query: {
        filter: {
          accountId,
          'createdBy.id': { $ne: userId },
          status: { $ne: 'Archived' },
        },
      },
    }),
  );

  if (_.isEmpty(response.data)) {
    return [];
  }
  let alAppsData = response.data.apps;
  let nextCursor = response.data.pagingMetadata.cursors.next;

  while (nextCursor) {
    const nextResponse = await httpClient.request(
      queryApp({ query: { cursorPaging: { cursor: nextCursor } } }),
    );

    alAppsData = alAppsData.concat(nextResponse.data.apps);
    nextCursor = nextResponse?.data?.pagingMetadata?.cursors?.next;
  }

  const latestVersionArr = await fetchLatestVersionMap(
    alAppsData.map((appData) => appData.id),
  );

  const appToLatestVersionMap = _.keyBy(latestVersionArr, 'id');

  return alAppsData.map((appData) => {
    return {
      appDefinitionId: appData.id,
      appDefinitionName: appData.name,
      latestVersion: appToLatestVersionMap[appData.id]?.fullVersion ?? '0.0.0',
    };
  });
};

const filterDeveloperAndContributionApps = (
  userWorkspaces: Account[],
  developerApps: AnyFixMe[],
) => {
  const userWorkspacesMap = userWorkspaces.reduce<Record<string, boolean>>(
    (map, { accountId }) => ({ ...map, [accountId]: true }),
    {},
  );

  return developerApps.filter((app) => !userWorkspacesMap[app.accountId]);
};

const getDeveloperApps = async (): Promise<DeveloperAppData[]> => {
  const response = await httpClient.request(
    queryMyApps({
      query: {
        sort: [
          {
            fieldName: 'appUpdatedDate',
            order: SortOrder.DESC,
          },
        ],
        cursorPaging: {
          limit: 100,
        },
      },
    }),
  );

  let allAppsData = response.data?.myApps ?? [];
  let nextCursor = response.data?.pagingMetadata?.cursors?.next;

  while (nextCursor) {
    const nextResponse = await httpClient.request(
      queryMyApps({
        query: {
          cursorPaging: {
            limit: 100,
            cursor: nextCursor,
          },
        },
      }),
    );
    allAppsData = [...allAppsData, ...(nextResponse.data?.myApps ?? [])];
    nextCursor = nextResponse?.data?.pagingMetadata?.cursors?.next;
  }

  return allAppsData.map((app) => {
    return {
      appDefinitionId: app.id,
      appDefinitionName: app.appData.appName,
      latestVersion: app.fullVersion,
      dateCreated: app.createdDate?.toDateString(),
      accountId: app.appData.accountId,
    };
  });
};

const getAllApps = async (
  userId: string,
  accountId: string,
): Promise<DeveloperAppData[]> => {
  if (experiment.isOpen('se_enableWorkspacesInPrivateApps')) {
    if (experiment.isOpen('se_useQueryMyAppsInBlocksAppsPanel')) {
      return getDeveloperApps();
    }

    const [accountApps, developerApps, userWorkspaces]: [
      DeveloperAppData[],
      DeveloperAppData[],
      Account[],
    ] = await Promise.all([
      getAccountOnlyApps(userId, accountId),
      getAvailableAppsOld(false),
      accountServerFacade.getUserWorkspaces(userId),
    ]);

    const isInWorkspace = userId !== accountId;

    if (isInWorkspace) {
      const workSpaceApps = developerApps.filter(
        (app) => app.accountId === accountId,
      );
      return [...accountApps, ...workSpaceApps];
    }

    return removeDuplicateApps([
      ...accountApps,
      ...filterDeveloperAndContributionApps(userWorkspaces, developerApps),
    ]);
  }

  const accountApps = await getAccountOnlyApps(userId, accountId);
  const developerApps = await getAvailableAppsOld(false);

  return removeDuplicateApps([...accountApps, ...developerApps]);
};

const removeDuplicateApps = (
  allAppsData: DeveloperAppData[],
): DeveloperAppData[] => {
  return _.uniqBy(allAppsData, 'appDefinitionId');
};

const getAvailableApps = async (userId: string, accountId: string) => {
  const allAppsData = await getAllApps(userId, accountId);

  const componentsRes = await getAppsComponents(allAppsData);

  const developerAppsWithComponents = allAppsData.map((app) => {
    const components = componentsRes[app.appDefinitionId]?.map((compData) =>
      convertComponentDataToOldComponentData(compData),
    );

    return {
      appDefinitionId: app.appDefinitionId,
      appDefinitionName: app.appDefinitionName,
      latestVersion: app.latestVersion,
      dateCreated: app.dateCreated,
      compTypes: _.uniq(
        components?.map((compData) => compData.compType).filter(Boolean),
      ),
      blocksVersion: getBlocksVersion(components),
      components,
    };
  });

  return Promise.resolve(developerAppsWithComponents);
};

const fetchListDraftAppsComponents = async (appIdArr: string[]) => {
  const response = await httpClient.request(
    listDraftAppsComponents({ appIds: appIdArr }),
  );
  return response.data.appToComponents;
};

const convertComponentDataToOldComponentData = (
  componentData: Component,
): ComponentOld => {
  return {
    compData: componentData.data as unknown as ComponentOld['compData'],
    compName: componentData.name,
    compType: componentData.type as unknown as ComponentOld['compType'],
    compId: componentData.id,
  };
};

const getAppsComponents = async (
  allApps: DeveloperAppData[],
): Promise<Record<string, Component[]>> => {
  const chuckedAppIds = _.chunk(
    allApps.map((app) => app.appDefinitionId),
    50,
  );
  const queryComponentPromiseArr = chuckedAppIds.map((appIdArr: string[]) => {
    return fetchListDraftAppsComponents(appIdArr);
  });
  const allComponentsRes = await Promise.all(queryComponentPromiseArr);

  const mergedMap = _.merge({}, ...allComponentsRes);

  return _.mapValues(mergedMap, 'components');
};

/**
 * used in case  se_getDeveloperAppsNewAPI=off
 * @deprecated use `getAvailableApps` instead
 */
const getAvailableAppsOld = (withComponents: boolean) => {
  return util.http
    .fetchJson(`${appsServiceBaseUrl}/apps?withComponents=${withComponents}`, {
      credentials: 'include',
    })
    .then((res: GetDeveloperAppsResponse) => {
      if (!res || !res.apps) {
        throw Error('Fetching apps from dev center failed');
      }
      return res.apps.map((app: GetAppInfoResponse) => ({
        appDefinitionId: app.appId,
        appDefinitionName: app.name,
        latestVersion: app.latestVersion,
        accountId: app.accountId,
        dateCreated: String(app.dateCreated),
        compTypes: _.uniq(
          app.components?.map((c: ComponentOld) => c.compType).filter(Boolean),
        ),

        blocksVersion: getBlocksVersion(app.components),
        components: app.components,
      }));
    });
};

const getBlocksVersion = (components: ComponentOld[]) => {
  const studioWidgetComponent = components?.find(
    ({ compType }) => compType === ComponentType.STUDIO_WIDGET,
  );

  if (!studioWidgetComponent) {
    // only Responsive Blocks (2.0.0) supports apps with CODE_PACKAGE or BACK_OFFICE_PAGE component
    const isResponsiveBlocks = components?.some(({ compType }) =>
      [ComponentType.CODE_PACKAGE, ComponentType.BACK_OFFICE_PAGE].includes(
        compType,
      ),
    );

    if (isResponsiveBlocks) return '2.0.0';
  }

  return studioWidgetComponent?.compData?.studioWidgetComponentData
    ?.blocksVersion;
};

const getAppInfoOld = (appDefIds: string[]) => {
  return util.http.fetchJson(
    `https://dev.wix.com/_api/app-service/v1/apps-info?apps-id=${appDefIds.join(
      '&apps-id=',
    )}`,
    { credentials: 'include' },
  );
};

const getAppSnapshotOld = (appDefinitionId: string) => {
  return util.http
    .fetchJson(`${APPS_SERVICES_BASE_URL}apps/${appDefinitionId}`)
    .then((res: GetAppSnapshotResponse) => {
      if (!res) {
        throw Error('Fetching app studio data from dev center failed');
      }
      return res;
    });
};

const getVersionsByAppId = async (appId: string) => {
  const appsVersionsResponse = await httpClient.request(
    queryAppVersion({
      query: {
        filter: { appId, state: 'PRODUCTION' },
        sort: [{ fieldName: 'updatedDate', order: SortOrder.DESC }],
      },
    }),
  );

  const oldVersionDataToNewVersionData = (appVersion: AppVersion) => {
    return {
      version: appVersion.fullVersion,
      dateUpdated: appVersion.updatedDate,
      dateCreated: appVersion.createdDate,
      status: 'RELEASED',
    };
  };

  return (
    (appsVersionsResponse?.data?.appVersions?.map(
      oldVersionDataToNewVersionData,
    ) as VersionsResponse[]) || []
  );
};
// todo: create experiments
const listingExperiment = experiment.isOpen('se_newDevCenterApiListing');
const namespaceExperiment = experiment.isOpen('se_newDevCenterApiNamespace');
const shareUrl = experiment.isOpen('se_newDevCenterApiShareUrl');

const fetchAppMarketData = async (apps: string[]) => {
  try {
    const { data } = await httpClient.request(
      queryMarketApp({
        query: {
          filter: {
            appId: apps,
          },
        },
      }),
    );

    return data?.marketApps;
  } catch (e) {
    return [];
  }
};

const fetchListLatestProductionVersions = async (appIds: string[]) => {
  const response = await httpClient.request(
    listLatestProductionVersions({ appIds }),
  );

  return response?.data?.appVersions;
};

export const getAppByVersion = async (
  appId: string,
  version: string,
  latestVersion: string,
) => {
  const { data: appByVersionResponse } = await httpClient.request(
    getAppByVersionRequest({
      appId,
      version,
    }),
  );

  const getAgregatedReleaseNotes = async () => {
    const listNotesResponse = await httpClient.request(
      listNotes({
        appId,
        version: util.appStudioUtils.getVersion(
          util.appStudioUtils.isTestVersion(version) ? latestVersion : version,
        ).major,
      }),
    );

    const notesArray = listNotesResponse?.data?.notes || [];
    return notesArray
      .filter((noteData) => !_.isEmpty(noteData.note))
      .map(
        (noteData) =>
          `${util.appStudioUtils.formatVersion(noteData.fullVersion)} ${
            noteData.note
          }`,
      );
  };

  const listReleaseNotes = await getAgregatedReleaseNotes();

  return {
    data: appByVersionResponse.data,
    listNotes: listReleaseNotes,
  };
};

export const devCenterFacade = {
  getDeveloperApps,
  getAvailableApps,
  getAvailableAppsOld,
  // todo: blocked by versions https://jira.wixpress.com/browse/WBL-4228
  getAppSnapshot: getAppSnapshotOld,
  getAppInfo: getAppInfoOld,
  // todo: namespace https://jira.wixpress.com/browse/WBL-4229 - createNamespacePanel.tsx
  namespaceSuggestion: namespaceExperiment,
  setNamespace: namespaceExperiment,
  // todo: listing https://jira.wixpress.com/browse/WBL-4231 - applicationStudioActions.ts
  updateName: listingExperiment,
  // todo: versions https://jira.wixpress.com/browse/WBL-4228 - fetchAppVersion.ts
  getAppByVersion,
  getVersionsByAppId,
  // todo: shareUrl https://jira.wixpress.com/browse/WBL-4230 - appLinkActions.ts
  fetchAppLinkFromDevCenter: shareUrl,
  createAppLinkFromDevCenter: shareUrl,
  revokeAppLinkFromDevCenter: shareUrl,
  fetchAppMarketData,
  fetchListLatestProductionVersions,
};
